"use client";

import { useState } from "react";
import { useSelector } from "react-redux";
import MatchTime from "../KbvMatchTime";
import OtherBetsDropdown from "../OtherBetsDropdown";
import { useMatches } from "@/hooks/useMatches";
import Loading from "../Loading";
import OddsPill from "../OddsPill";
import TeamRow from "../TeamRow";
import {
  LIVE_STATUSES,
  FINISHED_STATUSES,
  NOT_STARTED_STATUS,
} from "@/contants";
import { KEOBONGVIP_DOMAIN } from "@/config";
import { formatUnixToVNDate, formatHandicapLabelForDisplay } from "@/utils/format";
import { convertMatchTime } from "@/utils/convertMatchTime";
import ICON_ARROW_UP from "@/assets/images/arrow-up.svg";
import ICON_ARROW_UP_DARK from "@/assets/images/arrow_up_leagues.svg";
import ICON_NHANDINH from "@/assets/images/nhandinh.svg";
import ICON_DUDOAN_KIEMTIEN from "@/assets/images/mi-dudoan-kiemtien-green.svg";
import ICON_TATICS from "@/assets/images/tatics.svg";
import { useTheme } from "@/context/ThemeContext";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { getMatchHeaderType } from "@/utils/matchInfo";
import ThongTinTranDau from "../ThongTinTranDau";


export default function KiemTienMatch({ match }) {
  const { isDark } = useTheme();
  const { getMatchOdds } = useMatches();
  const { getScore, getOdds } = useLiveDataForMatch(match.id);
  const [openOtherBets, setOpenOtherBets] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
  const [showMatchInfoModal, setShowMatchInfoModal] = useState(false);
  const isShowInsight = match.status == "0" && match?.insight;
  const isShowDuDoanKiemTien = getMatchHeaderType(match?.status) === "upcoming";

  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const oddsPeriod = useSelector((state) => state.oddsPeriodReducer) || "full";
  const isHalf1 = oddsPeriod === "half1";

  const liveScore = getScore();
  const liveOdds = getOdds();
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);
  const odds = useMatchOdds(match, filterOdds, companyIdOdds);

  const {
    currentHomeScore,
    currentAwayScore,
    currentHomeHalfScore,
    currentAwayHalfScore,
    isHomeWin,
    isAwayWin,
    homeYellow,
    homeRed,
    awayYellow,
    awayRed,
    currentHomeCorner,
    currentAwayCorner,
  } = useMatchData(liveScore, match);

  const statusNum =
    liveScore?.status !== undefined
      ? Number(liveScore.status)
      : Number(match.status);
  const isNotStarted = NOT_STARTED_STATUS.includes(statusNum);
  const isLive = LIVE_STATUSES.includes(statusNum);
  const isFinished = FINISHED_STATUSES.includes(statusNum);

  const {
    date: formattedDate,
    thu: formattedThu,
    gio: formattedGio,
  } = formatUnixToVNDate(match?.matchTime);

  const handleOtherBetsButtonClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setOpenOtherBets((prev) => !prev);
    if (!openOtherBets && !otherOddsData) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => {
          if (data?.success) setOtherOddsData(data.data);
        })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const handleDataButtonClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setShowMatchInfoModal(true);
  };

  const handleInsightClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const slug = match?.insight?.slug;
    if (slug) {
      window.open(`${KEOBONGVIP_DOMAIN}/soi-keo/${slug}`);
    }
  };

  const handleDuDoanKiemTienClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const dateStr = match?.matchTime != null ? convertMatchTime(match.matchTime, "YYYY-MM-DD") : "";
    const dateParam = dateStr ? `&date=${dateStr}` : "";
    const url = `${KEOBONGVIP_DOMAIN}?showDuDoanKiemTien=1&matchId=${match.id}${dateParam}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="relative rounded-lg overflow-hidden bg-[var(--theme-bg)] hover:bg-[var(--theme-accent)] transition-colors cursor-pointer">
      <div className="flex">
        <div className="w-[57%] md:w-[44%] shrink-0">
          <table className="w-full table-fixed kbv-table">
            <colgroup>
              <col className="w-full" />
            </colgroup>
            <thead>
              <tr className="bg-[var(--theme-accent)]">
                <th className="text-xs px-2 py-2.5">
                  {isNotStarted ? (
                    <span className="flex font-medium text-xs text-left text-[var(--theme-text)]">{`${formattedThu} (${formattedDate}) ${formattedGio}`}</span>
                  ) : (
                    <MatchTime
                      homeCorner={currentHomeCorner}
                      awayCorner={currentAwayCorner}
                      isLive={isLive}
                      isFinished={isFinished}
                      isNotStarted={isNotStarted}
                      matchTime={match.matchTime}
                      liveScore={liveScore}
                      status={statusNum}
                      halfStartTime={match.halfStartTime}
                      textAlignment="left"
                      cornerStacked={false}
                    />
                  )}
                </th>
              </tr>
            </thead>
            <tbody>
              <TeamRow
                name={match.homeName}
                yellow={homeYellow}
                red={homeRed}
                score={currentHomeScore}
                halfScore={currentHomeHalfScore}
                isWin={isHomeWin}
                showScore={isLive || isFinished}
                direction="home"
              />
              <TeamRow
                name={match.awayName}
                yellow={awayYellow}
                red={awayRed}
                score={currentAwayScore}
                halfScore={currentAwayHalfScore}
                isWin={isAwayWin}
                showScore={isLive || isFinished}
                direction="away"
              />
              <tr>
                <td className="px-2 py-1">
                  {/* mobile */}
                  <div className="shrink-0 flex items-center justify-start gap-1 sm:hidden">
                    {isShowDuDoanKiemTien && (
                      <div
                        onClick={handleDuDoanKiemTienClick}
                        className="flex items-center gap-1 rounded-full overflow-hidden border border-[var(--theme-btn-border)] p-2 cursor-pointer hidden"
                      >
                        <img src={ICON_DUDOAN_KIEMTIEN} alt="" width={18} height={18} className="shrink-0 object-contain" aria-hidden />
                        <span className="text-xs font-semibold whitespace-nowrap truncate min-w-[20px]">kiếm tiền</span>
                      </div>
                    )}
                    {isShowInsight && (
                      <div
                        onClick={handleInsightClick}
                        className="flex items-center justify-center rounded-full border border-[var(--theme-btn-border)] w-[34px] h-[34px] p-2 cursor-pointer hidden"
                      >
                        <img src={ICON_NHANDINH} alt="" width={18} height={18} className="kbv-corner-icon shrink-0 object-contain" aria-hidden />
                      </div>
                    )}
                    <div
                      onClick={handleDataButtonClick}
                      className="flex items-center justify-center rounded-full border border-[var(--theme-btn-border)] w-[34px] h-[34px] p-2 cursor-pointer"
                    >
                      <img src={ICON_TATICS} alt="" width={18} height={18} className="kbv-corner-icon shrink-0 object-contain" aria-hidden />
                    </div>
                  </div>

                  {/* desktop */}
                  <div className="hidden sm:flex sm:flex-row sm:flex-wrap sm:items-center sm:gap-1.5">
                    {isShowDuDoanKiemTien && (
                      <button
                        type="button"
                        onClick={handleDuDoanKiemTienClick}
                        className="kbv-btn-du-doan-kiem-tien inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm transition-all hover:opacity-95 hidden"
                      >
                        <img src={ICON_DUDOAN_KIEMTIEN} alt="" className="h-[1.375rem] w-[1.375rem] shrink-0 object-contain" aria-hidden />
                        <span className="kbv-btn-du-doan-kiem-tien-text whitespace-nowrap">Dự đoán kiếm tiền</span>
                      </button>
                    )}
                    {isShowInsight && (
                      <button
                        onClick={handleInsightClick}
                        className="kbv-btn-nhan-dinh inline-flex items-center justify-center rounded-lg p-1.5 transition-all hover:opacity-95 hidden"
                      >
                        <img src={ICON_NHANDINH} alt="" className="kbv-corner-icon h-[1.375rem] w-[1.375rem] shrink-0 object-contain" aria-hidden />
                      </button>
                    )}
                    <button
                      onClick={handleDataButtonClick}
                      className="kbv-btn-du-lieu inline-flex items-center justify-center rounded-lg p-1.5 transition-all hover:opacity-95"
                    >
                      <img src={ICON_TATICS} alt="" className="kbv-corner-icon h-[1.375rem] w-[1.375rem] shrink-0 object-contain" aria-hidden />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex-1 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <table className={`w-full table-fixed border-collapse kbv-table ${isHalf1 ? "min-w-[280px]" : "min-w-[500px]"}`}>
            <colgroup>
              {isHalf1 ? (
                <>
                  <col className="w-[33.33%]" />
                  <col className="w-[33.33%]" />
                  <col className="w-[33.33%]" />
                </>
              ) : (
                <>
                  <col className="w-[20%]" />
                  <col className="w-[20%]" />
                  <col className="w-[20%]" />
                  <col className="w-[20%]" />
                  <col className="w-[20%]" />
                </>
              )}
            </colgroup>
            <thead>
              <tr className="bg-[var(--theme-accent)]">
                {isHalf1 ? (
                  <>
                    <th className="px-2 py-2.5 text-start">
                      <span className="text-[10px] font-semibold text-left text-[var(--theme-text)]">Cược chấp H1</span>
                    </th>
                    <th className="px-2 py-2.5 text-start">
                      <span className="text-[10px] font-semibold text-left text-[var(--theme-text)]">Tài Xỉu H1</span>
                    </th>
                    <th className="px-2 py-2.5 text-start">
                      <span className="text-[10px] font-semibold text-left text-[var(--theme-text)]">1x2 H1</span>
                    </th>
                  </>
                ) : (
                  <>
                    <th className="px-2 py-2.5 text-start">
                      <span className="text-[10px] font-semibold text-left text-[var(--theme-text)]">Chấp TT</span>
                    </th>
                    <th className="px-2 py-2.5 text-start">
                      <span className="text-[10px] font-semibold text-left text-[var(--theme-text)]">Tài Xỉu TT</span>
                    </th>
                    <th className="px-2 py-2.5 text-start">
                      <span className="text-[10px] font-semibold text-left text-[var(--theme-text)]">1x2 TT</span>
                    </th>
                    <th className="px-2 py-2.5 text-start">
                      <span className="text-[10px] font-semibold text-left text-[var(--theme-text)]">Cược chấp H1</span>
                    </th>
                    <th className="px-2 py-2.5 text-start">
                      <span className="text-[10px] font-semibold text-left text-[var(--theme-text)]">Tài Xỉu H1</span>
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              <tr>
                {isHalf1 ? (
                  <>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill label={formatHandicapLabelForDisplay(odds.handicapHalfValue, true)} odds={odds.homeHandicapHalfOdds} trend={odds.homeHandicapHalfTrend} />
                    </td>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill
                        label={odds.hasOverUnderHalfData ? odds.overUnderHalfValue : ""}
                        odds={odds.hasOverUnderHalfData ? odds.overHalfOdds : "-"}
                        trend={odds.hasOverUnderHalfData ? odds.overHalfTrend : "unchanged"}
                      />
                    </td>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill label="H" odds={odds.homeEuropeHalfOdds} trend={odds.homeEuropeHalfTrend} />
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill label={formatHandicapLabelForDisplay(odds.handicapValue, true)} odds={odds.homeHandicapOdds} trend={odds.homeHandicapTrend} />
                    </td>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill label={odds.overUnderValue || "2.50"} odds={odds.overOdds} trend={odds.overTrend} />
                    </td>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill label="H" odds={odds.homeEuropeOdds} trend={odds.homeEuropeTrend} />
                    </td>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill label={formatHandicapLabelForDisplay(odds.handicapHalfValue, true)} odds={odds.homeHandicapHalfOdds} trend={odds.homeHandicapHalfTrend} />
                    </td>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill
                        label={odds.hasOverUnderHalfData ? odds.overUnderHalfValue : ""}
                        odds={odds.hasOverUnderHalfData ? odds.overHalfOdds : "-"}
                        trend={odds.hasOverUnderHalfData ? odds.overHalfTrend : "unchanged"}
                      />
                    </td>
                  </>
                )}
              </tr>
              <tr>
                {isHalf1 ? (
                  <>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill label={formatHandicapLabelForDisplay(odds.handicapHalfValue, false)} odds={odds.awayHandicapHalfOdds} trend={odds.awayHandicapHalfTrend} />
                    </td>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill
                        label={odds.hasOverUnderHalfData ? "U" : ""}
                        odds={odds.hasOverUnderHalfData ? odds.underHalfOdds : "-"}
                        trend={odds.hasOverUnderHalfData ? odds.underHalfTrend : "unchanged"}
                      />
                    </td>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill label="A" odds={odds.awayEuropeHalfOdds} trend={odds.awayEuropeHalfTrend} />
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill label={formatHandicapLabelForDisplay(odds.handicapValue, false)} odds={odds.awayHandicapOdds} trend={odds.awayHandicapTrend} />
                    </td>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill label="U" odds={odds.underOdds} trend={odds.underTrend} />
                    </td>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill label="A" odds={odds.awayEuropeOdds} trend={odds.awayEuropeTrend} />
                    </td>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill label={formatHandicapLabelForDisplay(odds.handicapHalfValue, false)} odds={odds.awayHandicapHalfOdds} trend={odds.awayHandicapHalfTrend} />
                    </td>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill
                        label={odds.hasOverUnderHalfData ? "U" : ""}
                        odds={odds.hasOverUnderHalfData ? odds.underHalfOdds : "-"}
                        trend={odds.hasOverUnderHalfData ? odds.underHalfTrend : "unchanged"}
                      />
                    </td>
                  </>
                )}
              </tr>
              <tr>
                {isHalf1 ? (
                  <>
                    <td className="px-0 py-2 align-middle">
                      {match.countOtherOdds > 0 && (
                        <div className="flex justify-center items-center gap-1 cursor-pointer" onClick={handleOtherBetsButtonClick}>
                          <span className="text-xs font-medium text-[var(--theme-text)]">{match.countOtherOdds} cược khác</span>
                          <img src={isDark ? ICON_ARROW_UP_DARK : ICON_ARROW_UP} alt="arrow-down" width={16} height={16} className={`transition-transform duration-200 ${openOtherBets ? "rotate-180" : ""}`} />
                        </div>
                      )}
                    </td>
                    <td className="px-0.5 py-1 align-middle"></td>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill label="D" odds={odds.drawHalfOdds} trend={odds.drawHalfTrend} />
                    </td>
                  </>
                ) : (
                  <>
                    <td colSpan="2" className="px-0 py-2 align-middle">
                      {match.countOtherOdds > 0 && (
                        <div className="flex justify-center items-center gap-1 cursor-pointer" onClick={handleOtherBetsButtonClick}>
                          <span className="text-xs font-medium text-[var(--theme-text)]">{match.countOtherOdds} cược khác</span>
                          <img src={isDark ? ICON_ARROW_UP_DARK : ICON_ARROW_UP} alt="arrow-down" width={16} height={16} className={`transition-transform duration-200 ${openOtherBets ? "rotate-180" : ""}`} />
                        </div>
                      )}
                    </td>
                    <td className="px-0.5 py-1 align-middle">
                      <OddsPill label="D" odds={odds.instantDrawOdds} trend={odds.drawTrend} />
                    </td>
                    <td className="px-0.5 py-1 align-middle"></td>
                    <td className="px-0.5 py-1 align-middle"></td>
                  </>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="px-0 pt-2 align-middle">
        {match.countOtherOdds > 0 && openOtherBets && (
          <>
            {loadingOtherOdds && !otherOddsData && (
              <div className="py-4">
                <Loading />
              </div>
            )}
            {!loadingOtherOdds && otherOddsData && (
              <OtherBetsDropdown
                goalOddEvenOdds={otherOddsData?.goalOddEvenOdds}
                correctScoresOdds={otherOddsData?.correctScoresOdds}
                totalCornersOdds={otherOddsData?.totalCornersOdds}
                correctScoresHalfOdds={otherOddsData?.correctScoresHalfOdds}
                totalCornersHalfOdds={otherOddsData?.totalCornersHalfOdds}
              />
            )}
          </>
        )}
      </div>

      {showMatchInfoModal && (
        <ThongTinTranDau
          isOpen
          onClose={() => setShowMatchInfoModal(false)}
          match={match}
          matchInfo={{
            formattedDate,
            formattedThu,
            formattedGio,
            isLive,
            isFinished,
          }}
          statsInfo={{
            currentHomeScore,
            currentAwayScore,
            currentHomeHalfScore,
            currentAwayHalfScore,
            homeYellow,
            homeRed,
            awayYellow,
            awayRed,
            currentHomeCorner,
            currentAwayCorner,
          }}
        />
      )}
    </div>
  );
}
